import { rcApi, jwksApi } from './create-api';
import {
  rcApiResponseFormatter,
  decodeToken,
  getToken,
  isJWTExpired,
} from '@utils';
import config from '@config';

const { jwksAppName } = config;
const RC_PRODUCTION_URL = 'https://platform.ringcentral.com/restapi/v1.0';
let refreshingJwks = null;

const getCountryListUrl = '/dictionary/country';
const getStateListUrl = '/dictionary/state';
const getTimezoneListUri = '/dictionary/timezone';
const getGreetingsLanguageUri = '/dictionary/language';
const getUserGreetingListUri = '/dictionary/greeting';

export const validateJwks = async (
  token,
  accountUid,
  appName = jwksAppName.production
) => {
  if (isJWTExpired(token, true)) {
    refreshingJwks = refreshingJwks || getAccountJwksToken(accountUid, appName);
    const { data, error } = await refreshingJwks;

    if (data) {
      return {
        token: data,
        hasUpdatedToken: true,
      };
    }

    refreshingJwks = false;
    return { error };
  }

  refreshingJwks = false;
  return { token };
};

const fetchJwksToken = async (accountId, appName) => {
  const decodedToken = decodeToken(getToken());

  return await rcApiResponseFormatter(
    rcApi.post(
      config.rc_jwks_url,
      {
        appName,
        accountId,
      },
      {
        headers: {
          access_token: decodedToken.rc_access_token,
        },
      }
    )
  );
};

const getAccountJwksToken = async (
  accountUid,
  appName = jwksAppName.production
) => {
  if ((config.rcApiBaseUrl || '').includes('devtest')) {
    await fetchJwksToken(accountUid, jwksAppName.production);
  }

  const { data, error } = await fetchJwksToken(accountUid, appName);

  if (data) {
    let exp = new Date();
    exp = exp.setSeconds(exp.getSeconds() + data.expires_in);
    exp = Math.round(exp / 1000);

    return {
      data: {
        accessToken: data.access_token,
        exp,
      },
      error,
    };
  }

  return { error };
};

const getCountryList = async token => {
  const result = await rcApiResponseFormatter(
    rcApi.get(`${getCountryListUrl}?perPage=1000`, {
      responseType: 'array',
    })
  );

  if (!result.error) {
    // return result.data.map(record => {
    //   return { id: record.id, name: record.isoCode };
    // });
    return result;
  } else {
    return [];
  }
};

const getTimezoneList = async token => {
  const result = await rcApiResponseFormatter(
    rcApi.get(`${getTimezoneListUri}?perPage=1000`, {
      responseType: 'array',
    })
  );

  if (!result.error) {
    // return {
    //   data: result.data.map(record => {
    //     return {
    //       id: record.id,
    //       name: record.name,
    //     };
    //   }),
    // };
    return result;
  } else {
    return [];
  }
};

const getStateList = async countryId => {
  const result = await rcApiResponseFormatter(
    rcApi.get(`${getStateListUrl}?perPage=1000&countryId=${countryId}`, {
      responseType: 'array',
    })
  );

  if (!result.error) {
    // return result.data.map(record => {
    //   return { id: record.id, name: record.isoCode };
    // });
    return result;
  } else {
    return [];
  }
};

const getGreetingsLanguageList = async token => {
  const result = await rcApiResponseFormatter(
    rcApi.get(`${getGreetingsLanguageUri}?perPage=1000`, {
      responseType: 'array',
    })
  );

  if (!result.error) {
    // return result.data.map(record => {
    //   return { id: record.id, name: record.name };
    // });
    return result;
  } else {
    return [];
  }
};

const getUserGreetingList = async token => {
  const result = await rcApiResponseFormatter(
    rcApi.get(`${getGreetingsLanguageUri}?perPage=1000&type=introductory`, {
      responseType: 'array',
    })
  );

  if (!result.error) {
    return result.data.map(record => {
      return { id: record.id, name: record.name };
    });
  } else {
    return [];
  }
};

const getGretingList = async type => {
  const result = await rcApiResponseFormatter(
    rcApi.get(`${getUserGreetingListUri}?perPage=1000&type=${type}`, {
      responseType: 'array',
    })
  );

  if (!result.error) {
    return result.data;
  } else {
    return [];
  }
};

const validateEmergencyAddress = async (address, token) => {
  const result = await rcApiResponseFormatter(
    rcApi.post('address-validation/emergency/validate', address, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  );
  return result;
};

// const getDeviceModelList = async () => {
//   return await rcApiResponseFormatter(rcApi.get('dictionary/device'));
// };

const getDeviceModelList = async () => {
  // return await rcApiResponseFormatter(rcApi.get('https://9x6khbqf0h.execute-api.us-east-2.amazonaws.com/default/temp-device-dictionary'));

  const response = await fetch(
    'https://9x6khbqf0h.execute-api.us-east-2.amazonaws.com/default/temp-device-dictionary'
  );

  const data = await response.json();

  return {
    data: data.records,
    error: data.error || null,
  };
};

const getStandardRoles = async () => {
  return await rcApiResponseFormatter(
    rcApi.get('/dictionary/user-role', {
      responseType: 'array',
    })
  );
};

const getPermissionCategory = async (accountUid, token) => {
  return await rcApiResponseFormatter(
    rcApi.get('/dictionary/permission-category')
  );
};

const getAllPermissions = async (accountUid, token) => {
  return await rcApiResponseFormatter(rcApi.get('/dictionary/permission'));
};

const getUserRoles = async (accountUid, token) => {
  return await rcApiResponseFormatter(
    jwksApi.get(`${RC_PRODUCTION_URL}/account/${accountUid}/user-role`, {
      hasToken: token,
      accountUid,
      headers: {
        Authorization: `Bearer ${token && token.accessToken}`,
      },
    })
  );
};

const upsertUserRole = async (roleData, accountUid, roleId, token) => {
  const headerConfig = {
    hasToken: token,
    accountUid,
    headers: {
      Authorization: `Bearer ${token && token.accessToken}`,
    },
  };

  return roleId
    ? await rcApiResponseFormatter(
        jwksApi.put(
          `${RC_PRODUCTION_URL}/account/${accountUid}/user-role/${roleId}`,
          roleData,
          headerConfig
        )
      )
    : await rcApiResponseFormatter(
        jwksApi.post(
          `${RC_PRODUCTION_URL}/account/${accountUid}/user-role`,
          roleData,
          headerConfig
        )
      );
};

const deleteCustomUserRole = async (accountUid, roleId, token) => {
  return await rcApiResponseFormatter(
    jwksApi.delete(
      `${RC_PRODUCTION_URL}/account/${accountUid}/user-role/${roleId}`,
      {
        hasToken: token,
        accountUid,
        headers: {
          Authorization: `Bearer ${token && token.accessToken}`,
        },
      }
    )
  );
};

const getTemplates = async (accountUid, token) => {
  return await rcApiResponseFormatter(
    jwksApi.get(`${RC_PRODUCTION_URL}/account/${accountUid}/templates`, {
      hasToken: token,
      accountUid,
      headers: {
        Authorization: `Bearer ${token && token.accessToken}`,
      },
    })
  );
};

const getTemporaryNumbers = async (accountUid, token) => {
  return await rcApiResponseFormatter(
    jwksApi.get(`${RC_PRODUCTION_URL}/account/${accountUid}/phone-number`, {
      hasToken: token,
      headers: {
        Authorization: `Bearer ${token && token.accessToken}`,
      },
    })
  );
};

const getNotes = async chatId => {
  return rcApiResponseFormatter(rcApi.get(`/glip/chats/${chatId}/notes`));
};

const createNote = async (note, chatId) => {
  return rcApiResponseFormatter(
    rcApi.post(`/glip/chats/${chatId}/notes`, {
      title: note.title,
      body: note.body,
    })
  );
};

const getNote = async noteId => {
  return rcApiResponseFormatter(rcApi.get(`/glip/notes/${noteId}`));
};

const updateNote = async note => {
  return rcApiResponseFormatter(
    rcApi.patch(`glip/notes/${note.id}`, { title: note.title, body: note.body })
  );
};

const publishNote = async noteId => {
  return rcApiResponseFormatter(rcApi.post(`glip/notes/${noteId}/publish`));
};

const deleteNote = async noteId => {
  return rcApiResponseFormatter(rcApi.delete(`/glip/notes/${noteId}`));
};

const getAllDevices = async (accountUid, token, query) => {
  return new Promise(async (resolve, reject) => {
    const devices = [];
    let pageCounter = 1;
    const { data, error, paging } = await rcApiResponseFormatter(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/device?perPage=1000`,
        {
          hasToken: token,
          accountUid,
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      devices.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await rcApiResponseFormatter(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/device?page=${pageCounter}&perPage=1000`,
              {
                hasToken: token,
                accountUid,
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            devices.push(...data);
          }
        }
        resolve({
          data: devices,
          error: null,
        });
      } else {
        resolve({
          data: devices,
          error: null,
        });
      }
    }
  });
};
const getAllExtensions = async (accountUid, token, query) => {
  return new Promise(async (resolve, reject) => {
    const extensions = [];
    let pageCounter = 1;
    const { data, error, paging } = await rcApiResponseFormatter(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/extension?perPage=1000`,
        {
          hasToken: token,
          accountUid,
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      extensions.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await rcApiResponseFormatter(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/extension?page=${pageCounter}&perPage=1000`,
              {
                hasToken: token,
                accountUid,
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            extensions.push(...data);
          }
        }
        resolve({
          data: extensions,
          error: null,
        });
      } else {
        resolve({
          data: extensions,
          error: null,
        });
      }
    }
  });
};
const getAllSites = async (accountUid, token, query) => {
  return new Promise(async (resolve, reject) => {
    const sites = [];
    let pageCounter = 1;
    const { data, error, paging } = await rcApiResponseFormatter(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/sites?perPage=1000`,
        {
          hasToken: token,
          accountUid,
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      sites.push(...data);
      if (paging?.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await rcApiResponseFormatter(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/sites?page=${pageCounter}&perPage=1000`,
              {
                hasToken: token,
                accountUid,
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            sites.push(...data);
          }
        }
        resolve({
          data: sites,
          error: null,
        });
      } else {
        resolve({
          data: sites,
          error: null,
        });
      }
    }
  });
};

const getSipDevice = async (
  accountUid,
  deviceId = '~',
  token,
  rateLimitReachedCallback
) => {
  return new Promise(async (resolve, reject) => {
    const { data, error, paging } = await rcApiResponseFormatter(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/~/device/${deviceId}/sip-info/`,
        {
          hasToken: token,
          accountUid,
          hooks: { rateLimitReached: rateLimitReachedCallback },
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      resolve({
        data,
        error: null,
      });
    }
  });
};

const getAllUsers = async (accountId = '~') => {
  return new Promise(async (resolve, reject) => {
    const allUsers = [];
    let pageCounter = 1;
    const { data, error, paging } = await rcApiResponseFormatter(
      rcApi.get(`/account/${accountId}/extension?type=User&perPage=1000`)
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      allUsers.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await rcApiResponseFormatter(
            rcApi.get(
              `/account/${accountId}/extension?type=User&page=${pageCounter}&perPage=1000`
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            allUsers.push(...data);
          }
        }

        resolve({
          data: allUsers,
          error: null,
        });
      } else {
        resolve({
          data: allUsers,
          error: null,
        });
      }
    }
  });
};

const getAllTeams = async accountId => {
  return new Promise(async (resolve, reject) => {
    const allTeams = [];
    const {
      data,
      error,
      navigation = {},
    } = await rcApiResponseFormatter(rcApi.get(`/glip/teams?recordCount=250`));

    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      allTeams.push(...data);
      let nextPageToken = navigation.nextPageToken;
      if (nextPageToken) {
        while (nextPageToken) {
          const {
            data,
            error,
            navigation = {},
          } = await rcApiResponseFormatter(
            rcApi.get(`/glip/teams?recordCount=250&pageToken=${nextPageToken}`)
          );

          nextPageToken = navigation.nextPageToken;
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            allTeams.push(...data);
          }
        }

        resolve({
          data: allTeams,
          error: null,
        });
      } else {
        resolve({
          data: allTeams,
          error: null,
        });
      }
    }
  });
};

const createTeam = data => {
  return rcApiResponseFormatter(rcApi.post('glip/teams', data));
};

const getSecretQuestions = async () => {
  return rcApiResponseFormatter(rcApi.get(`dictionary/secret-question`));
};

export default {
  getAccountJwksToken,
  getGretingList,
  getTimezoneList,
  getCountryList,
  getStateList,
  getGreetingsLanguageList,
  getUserGreetingList,
  validateEmergencyAddress,
  getDeviceModelList,
  getUserRoles,
  getStandardRoles,
  getAllPermissions,
  getPermissionCategory,
  upsertUserRole,
  deleteCustomUserRole,
  getTemplates,
  getTemporaryNumbers,
  getNotes,
  createNote,
  getNote,
  updateNote,
  publishNote,
  deleteNote,
  getAllExtensions,
  getSecretQuestions,
  getAllUsers,
  getAllSites,
  getAllTeams,
  createTeam,
  getAllDevices,
  getSipDevice,
};
